<script>

import Layout from '@/components/pages/layouts/vertical'
import { minLength, sameAs, required } from "vuelidate/lib/validators";
import { mapActions, mapState } from 'vuex';

export default {
  components: {
    Layout,
  },
  data() {
    return {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      currentPassword: "",
      confirmedPassword: "",
      password: "",
      submitted: false,
    };
  },
  created() {
    this.firstName = this.user.firstName;
    this.lastName = this.user.lastName;
    this.phoneNumber = this.user.phoneNumber;
  },
  validations() {
    const defaultValidationsList = {
      password: {},
      confirmedPassword: {},
      currentPassword: {}
    };
    const noPasswordValidationsNeeded =
      !this.currentPasswordExists() &&
      !this.passwordExists() &&
      !this.confirmedPasswordExists();

    if (noPasswordValidationsNeeded) return defaultValidationsList;

    return {
      password: {
        required,
        minLength: minLength(6)
      },
      confirmedPassword: {
        required,
        minLength: minLength(6),
        sameAs: sameAs('password'),
      },
      currentPassword: {
        required
      }
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('notification', {
      notificationType: state => state.type,
      notificationMessage: state => state.message
    })
  },
  methods: {
    ...mapActions('auth', ['updateUser']),
    currentPasswordExists() {
      return this.currentPassword && this.currentPassword.length > 0;
    },
    passwordExists() {
      return this.password && this.password.length > 0;
    },
    confirmedPasswordExists() {
      return this.confirmedPassword && this.confirmedPassword.length > 0;
    },
    tryToUpdate() {
      this.submitted = true;
      this.$v.$touch();

      if(this.$v.$invalid) {
        return;
      }

      let payload = {
        first_name: this.firstName,
        last_name: this.lastName,
        phone_number: this.phoneNumber
      };
      if(this.currentPasswordExists()) {
        payload = {
          ...payload,
          current_password: this.currentPassword,
          password_confirmation: this.confirmedPassword,
          password: this.password
        }
      }
      this.updateUser(payload);
    }
  }
};
</script>

<template>
  <Layout>
    <div class="row">
      <div class="col-xl-9">
        <div class="card overflow-hidden">
          <div class="col-7">
              <div class="text-primary p-3">
                <h5 class="text-primary">Change Account Settings</h5>
              </div>
            </div>
          <div class="card">
            <div class="card-body">
              <div v-if="notificationMessage" :class="'alert ' + notificationType">
                {{ notificationMessage }}
              </div>
              <b-form @submit.prevent="tryToUpdate">
                <b-form-group
                  class="mb-3"
                  label="First Name"
                  label-for="firstname-input"
                  label-cols-sm="3"
                >
                  <b-form-input
                    id="firstname-input"
                    type="text"
                    v-model="firstName"
                  ></b-form-input>
                </b-form-group>

                <b-form-group
                  class="mb-3"
                  label="Last Name"
                  label-for="lastname-input"
                  label-cols-sm="3"
                >
                  <b-form-input
                    id="lastname-input"
                    type="text"
                    v-model="lastName"
                  ></b-form-input>
                </b-form-group>

                <b-form-group
                  class="mb-3"
                  label="Phone Number"
                  label-for="phonenumber-input"
                  label-cols-sm="3"
                >
                  <b-form-input
                    id="phonenumber-input"
                    type="text"
                    v-model="phoneNumber"
                  ></b-form-input>
                </b-form-group>

                <b-form-group
                  class="mb-4"
                  label="Current Password"
                  label-for="password-current"
                  label-cols-sm="3"
                >
                  <b-form-input
                    id="password-current"
                    type="password"
                    v-model="currentPassword"
                    :class="{ 'is-invalid': submitted && $v.currentPassword.$invalid,
                    'is-valid': submitted && !$v.currentPassword.$error}"
                   ></b-form-input>
                   <b-form-invalid-feedback>
                     <span v-if="!$v.currentPassword.required">
                       Current password is required.
                     </span>
                   </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                 class="mb-4"
                 label="Password"
                 label-for="password"
                 label-cols-sm="3"
                >
                  <b-form-input
                   id="password"
                   type="password"
                   v-model="password"
                   :class="{ 'is-invalid': submitted && $v.password.$invalid,
                   'is-valid': submitted && !$v.password.$error}"
                   aria-describedby="invalid-feedback">
                  </b-form-input>
                  <b-form-invalid-feedback>
                    <span v-if="!$v.password.required">
                      Please enter password.
                    </span>
                    <span v-if="$v.password.required && !$v.password.$minLength">
                      Password must be at least 6 characters.
                    </span>
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                 class="mb-4"
                 label="Confirm Password"
                 label-for="confirmedpassword-input"
                 label-cols-sm="3"
                >
                  <b-form-input
                    id="confirmedpassword-input"
                    type="password"
                    v-model="confirmedPassword"
                    :class="{ 'is-invalid': submitted && $v.confirmedPassword.$invalid,
                    'is-valid': submitted && !$v.confirmedPassword.$error,
                     } "
                    aria-describedby="invalid-feedback">
                  </b-form-input>
                  <b-form-invalid-feedback>
                    <span v-if="!$v.confirmedPassword.required">
                      Password Confirmation is required.
                    </span>
                    <span v-if="$v.confirmedPassword.required && !$v.confirmedPassword.sameAs">
                      Please enter the same password.
                    </span>
                    <span v-if="$v.confirmedPassword.required && $v.confirmedPassword.sameAs && !$v.confirmedPassword.$minLength">
                      Password must be at least 6 characters.
                    </span>
                  </b-form-invalid-feedback>
                </b-form-group>

                <div class="row justify-content-end">
                  <div class="col-xl-9">
                    <div>
                      <b-button type="submit" variant="primary">Submit</b-button>
                    </div>
                  </div>
                </div>
              </b-form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
